/**
 * useBatteryStatus Hook
 *
 * Wraps the Battery Status API to detect low battery and battery saver mode.
 * Useful for warning users about potentially slow uploads when battery is low.
 *
 * @example
 * const { isLowBattery, isBatterySaverLikely } = useBatteryStatus()
 *
 * if (isBatterySaverLikely) {
 *   showSlowUploadWarning()
 * }
 */

import { useState, useEffect } from 'react'

export interface UseBatteryStatusReturn {
  /**
   * Current battery level (0-1, where 1 = 100%)
   */
  batteryLevel: number

  /**
   * Whether the device is currently charging
   */
  isCharging: boolean

  /**
   * Whether battery is below 20% (low battery threshold)
   */
  isLowBattery: boolean

  /**
   * Whether battery saver mode is likely active
   * (battery < 20% and not charging)
   */
  isBatterySaverLikely: boolean

  /**
   * Whether the Battery Status API is supported
   */
  supported: boolean
}

interface BatteryManager extends EventTarget {
  charging: boolean
  chargingTime: number
  dischargingTime: number
  level: number
  addEventListener(type: 'chargingchange', listener: (event: Event) => void): void
  addEventListener(type: 'levelchange', listener: (event: Event) => void): void
  removeEventListener(type: 'chargingchange', listener: (event: Event) => void): void
  removeEventListener(type: 'levelchange', listener: (event: Event) => void): void
}

/**
 * Hook for Battery Status API
 *
 * Detects battery level and charging state. Infers battery saver mode when
 * battery is low (<20%) and device is not charging.
 *
 * Note: Battery Status API is deprecated in some browsers due to privacy
 * concerns. Falls back to unsupported state gracefully.
 *
 * @returns Battery status state
 */
export function useBatteryStatus(): UseBatteryStatusReturn {
  const [batteryState, setBatteryState] = useState<UseBatteryStatusReturn>({
    batteryLevel: 1,
    isCharging: false,
    isLowBattery: false,
    isBatterySaverLikely: false,
    supported: false
  })

  useEffect(() => {
    // Check if Battery API is supported
    if (typeof navigator === 'undefined' || !('getBattery' in navigator)) {
      return
    }

    let battery: BatteryManager | null = null

    /**
     * Updates battery state from the battery manager
     */
    const updateBatteryState = (batteryManager: BatteryManager) => {
      const level = batteryManager.level
      const charging = batteryManager.charging
      const isLowBattery = level < 0.2 // Below 20%
      const isBatterySaverLikely = isLowBattery && !charging

      setBatteryState({
        batteryLevel: level,
        isCharging: charging,
        isLowBattery,
        isBatterySaverLikely,
        supported: true
      })
    }

    /**
     * Initialize battery monitoring
     */
    const initBattery = async () => {
      try {
        battery = await (navigator as any).getBattery()

        if (battery) {
          // Initial state
          updateBatteryState(battery)

          // Event listeners for changes
          const handleChargingChange = () => {
            if (battery) updateBatteryState(battery)
          }

          const handleLevelChange = () => {
            if (battery) updateBatteryState(battery)
          }

          battery.addEventListener('chargingchange', handleChargingChange)
          battery.addEventListener('levelchange', handleLevelChange)

          // Cleanup function
          return () => {
            if (battery) {
              battery.removeEventListener('chargingchange', handleChargingChange)
              battery.removeEventListener('levelchange', handleLevelChange)
            }
          }
        }
      } catch (error) {
        // Battery API not supported or blocked
        console.debug('[useBatteryStatus] Battery API not available:', error)
        setBatteryState({
          batteryLevel: 1,
          isCharging: false,
          isLowBattery: false,
          isBatterySaverLikely: false,
          supported: false
        })
      }
    }

    const cleanupPromise = initBattery()

    return () => {
      // Cleanup event listeners
      cleanupPromise.then((cleanup) => {
        if (cleanup) cleanup()
      })
    }
  }, [])

  return batteryState
}
